import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  BackendService,
  HttpOptions,
  PaginatedResponseModel,
} from '@ipg/ngx-frontend-toolset';
import { ClientEventConflictResponseModel } from '../models/clients.model';
import { Interaction } from '../models/interactions.model';
import {
  TemplateMoveCreatedResponse,
  TemplateMoveInfoResponse,
  TemplateMovePreviewResponse,
  TemplateMoveRequest,
} from '../views/client-interactions/models/move-template.models';

@Injectable({
  providedIn: 'root',
})
export class InteractionService {
  private readonly _backend = inject(BackendService);

  getById(id: string, options?: HttpOptions): Observable<Interaction> {
    return this._backend.get(`/interactions/${id}`, options);
  }

  getAll(options?: HttpOptions): Observable<PaginatedResponseModel<Interaction[]>> {
    return this._backend.get(`/interactions`, options);
  }

  create(data: unknown, options?: HttpOptions): Observable<unknown> {
    return this._backend.post(`/interactions`, data, options);
  }

  update(id: string, data: unknown, options?: HttpOptions): Observable<unknown> {
    return this._backend.put(`/interactions/${id}`, data, options);
  }

  delete(correlationId: string, options?: HttpOptions): Observable<unknown> {
    return this._backend.delete(`/interactions/${correlationId}`, options);
  }

  duplicate(id: string, options?: HttpOptions) {
    return this._backend.post(`/interactions/${id}/duplicate`, null, options);
  }

  publish(id: string) {
    return this._backend.put(`/interactions/${id}/publish`, null);
  }

  reject(id: string) {
    return this._backend.put(`/interactions/${id}/reject`, null);
  }

  createQuestion(interactionId: string, data: unknown) {
    return this._backend.post(`/interactions/${interactionId}/questions`, data);
  }

  updateQuestion(interactionId: string, questionId: string, data: unknown) {
    return this._backend.put(
      `/interactions/${interactionId}/questions/${questionId}`,
      data
    );
  }

  sortQuestion(interactionId: string, questionId: string, data: unknown) {
    return this._backend.put(
      `/interactions/${interactionId}/questions/${questionId}/sort`,
      data
    );
  }

  deleteQuestion(interactionId: string, questionId: string) {
    return this._backend.delete(
      `/interactions/${interactionId}/questions/${questionId}`
    );
  }

  createAnswer(interactionId: string, data: unknown) {
    return this._backend.post(`/interactions/${interactionId}/answers`, data);
  }

  updateAnswer(interactionId: string, answerId: string, data: unknown) {
    return this._backend.put(
      `/interactions/${interactionId}/answers/${answerId}`,
      data
    );
  }

  sortAnswer(interactionId: string, answerId: string, data: unknown) {
    return this._backend.put(
      `/interactions/${interactionId}/answers/${answerId}/sort`,
      data
    );
  }

  deleteAnswer(interactionId: string, answerId: string) {
    return this._backend.delete(`/interactions/${interactionId}/answers/${answerId}`);
  }

  createAction(interactionId: string, data: unknown) {
    return this._backend.post(`/interactions/${interactionId}/actions`, data);
  }

  updateAction(interactionId: string, actionId: string, data: unknown) {
    return this._backend.put(
      `/interactions/${interactionId}/actions/${actionId}`,
      data
    );
  }

  sortAction(interactionId: string, actionId: string, data: unknown) {
    return this._backend.put(
      `/interactions/${interactionId}/actions/${actionId}/sort`,
      data
    );
  }

  deleteAction(interactionId: string, actionId: string) {
    return this._backend.delete(`/interactions/${interactionId}/actions/${actionId}`);
  }

  updateVariables(interactionId: string, data: unknown) {
    return this._backend.put(`/interactions/${interactionId}/variables`, data);
  }

  getConflicts(interactionId: string): Observable<ClientEventConflictResponseModel[]> {
    return this._backend.get(`/interactions/${interactionId}/conflicts`);
  }

  attachTemplateToClient(interactionId: string, data: { clientId: string }) {
    return this._backend.post(`/interactions/${interactionId}/attach-to-client`, data);
  }

  attachTemplatesToClient(data: { clientId: string; templateIds: string[] }) {
    return this._backend.post(`/interactions/attach-to-client`, data);
  }

  share(correlationId: string, options?: HttpOptions): Observable<string> {
    return this._backend.post<string>(
      `/interactions/${correlationId}/share`,
      null,
      options
    );
  }

  unshare(correlationId: string, options?: HttpOptions): Observable<unknown> {
    return this._backend.delete(`/interactions/${correlationId}/share`, options);
  }

  getMoveInfo(
    correlationId: string,
  ): Observable<TemplateMoveInfoResponse> {
    return this._backend.get(
      `/interactions/${correlationId}/move/info`,
    );
  }

  getMovePreview(
    correlationId: string,
    targetUnitId: string
  ): Observable<TemplateMovePreviewResponse> {
    return this._backend.get(
      `/interactions/${correlationId}/move/preview`,
      { params: { targetUnitId } }
    );
  }

  moveTemplate(
    correlationId: string,
    data: TemplateMoveRequest
  ): Observable<TemplateMoveCreatedResponse> {
    return this._backend.post<TemplateMoveCreatedResponse>(
      `/interactions/${correlationId}/move`,
      data
    );
  }
}
